/**
 * HubSpot Blocked Email Domains
 * 
 * List of free email domains that HubSpot blocks by default.
 * Based on: https://knowledge.hubspot.com/forms/what-domains-are-blocked-when-using-the-forms-email-domains-to-block-feature
 * 
 * This is a subset of the most common blocked domains. The full list is extensive.
 * HubSpot may block additional domains not listed here.
 */

export const HUBSPOT_BLOCKED_DOMAINS = new Set([
  // Major free email providers
  "gmail.com",
  "yahoo.com",
  "hotmail.com",
  "outlook.com",
  "aol.com",
  "icloud.com",
  "mail.com",
  "protonmail.com",
  "yandex.com",
  "zoho.com",
  "gmx.com",
  "live.com",
  "msn.com",
  "ymail.com",
  "mail.ru",
  "inbox.com",
  "fastmail.com",
  "tutanota.com",
  
  // Disposable/temporary email services
  "mailinator.com",
  "10minutemail.com",
  "guerrillamail.com",
  "tempmail.com",
  "throwaway.email",
  "dispostable.com",
  "mohmal.com",
  "temp-mail.org",
  "getnada.com",
  "maildrop.cc",
  "mintemail.com",
  "sharklasers.com",
  "getairmail.com",
  "meltmail.com",
  "trashmail.com",
  "emailondeck.com",
  "fakeinbox.com",
  "mytemp.email",
  "tempmailo.com",
  "throwawaymail.com",
  "mailnesia.com",
  "mox.pp.ua",
  "spamgourmet.com",
  "spamhole.com",
  "spamtraps.com",
  "tempail.com",
  "tempe-mail.com",
  "tempinbox.co.uk",
  "tempmail.de",
  "tempmail.net",
  "tempmail.us",
  "tempmail2.com",
  "tempmailer.com",
  "tempmailer.de",
  "tempthe.net",
  "yopmail.com",
  "yopmail.fr",
  "yopmail.net",
  
  // Additional common free providers
  "qq.com",
  "163.com",
  "126.com",
  "sina.com",
  "sohu.com",
  "foxmail.com",
  "rediffmail.com",
  "mail.com",
  "email.com",
  "aim.com",
  "rocketmail.com",
  "earthlink.net",
  "comcast.net",
  "verizon.net",
  "att.net",
  "sbcglobal.net",
  "cox.net",
  "charter.net",
  "optonline.net",
  "roadrunner.com",
  "bellsouth.net",
  "windstream.net",
  "frontier.com",
  "centurytel.net",
  "embarqmail.com",
  "netzero.com",
  "netzero.net",
  "juno.com",
  "excite.com",
  "lycos.com",
  "mail.com",
  "email.com",
  "usa.com",
  "iname.com",
  "writeme.com",
  "mynet.com",
  "myway.com",
  "myrealbox.com",
  "myownemail.com",
  "mypersonalemail.com",
  "myemail.com",
  "mymail.com",
  "myinbox.com",
  "mybox.com",
  "mymailbox.com",
  "myemailbox.com",
  "myemailaddress.com",
  "myemailaccount.com",
  "myemailservice.com",
  "myemailprovider.com",
  "myemailhost.com",
  "myemailserver.com",
  "myemaildomain.com",
  "myemailhosting.com",
  "myemailserviceprovider.com",
]);

/**
 * Checks if an email domain is blocked by HubSpot
 * @param email - The email address to check
 * @returns true if the domain is blocked, false otherwise
 */
export function isHubSpotBlockedDomain(email: string): boolean {
  if (!email || !email.includes("@")) {
    return false;
  }

  const domain = email.split("@")[1]?.toLowerCase();
  if (!domain) {
    return false;
  }

  return HUBSPOT_BLOCKED_DOMAINS.has(domain);
}

/**
 * Gets a user-friendly error message for blocked domains
 * @param email - The email address that was blocked
 * @returns Error message explaining why the email is blocked
 */
export function getHubSpotBlockedDomainMessage(email: string): string {
  const domain = email.split("@")[1]?.toLowerCase();
  return `This email domain (${domain}) is not accepted. Please use a business or professional email address instead of a free email provider.`;
}
